import { useState } from "react";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Stat, Section, Loading, Empty, useFetch, money, dt } from "./_shared";

function AffiliateListPanel({ status, mode, title, emptyText }) {
  const { data, loading, reload } = useFetch(`/admin/affiliate/applications?status=${status}`);
  const apps = data?.applications || [];
  const [busy, setBusy] = useState(null);
  const [pct, setPct] = useState({});

  const decide = async (app, approve) => {
    setBusy(app.user_id + (approve ? "-a" : "-r"));
    try {
      if (app.source === "application") {
        await api.post(`/admin/affiliate-applications/${app.user_id}/decide`, {
          approve,
          rejection_reason: approve ? null : "",
        });
      } else {
        const path = approve
          ? `/admin/affiliate/${app.user_id}/approve`
          : `/admin/affiliate/${app.user_id}/reject`;
        await api.post(path, approve ? { commission_percent: Number(pct[app.user_id] ?? 20) } : { reason: "" });
      }
      toast.success(approve ? "Als Affiliate freigeschaltet." : (mode === "active" ? "Affiliate-Status entzogen." : "Bewerbung abgelehnt."));
      await reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Section title={`${title}${apps.length ? ` · ${apps.length}` : ""}`} testId={`admin-aff-${mode}`}>
      {loading ? (
        <Loading />
      ) : apps.length === 0 ? (
        <Empty text={emptyText} />
      ) : (
        <div className="space-y-2">
          {apps.map((a) => (
            <div key={a.user_id} className="rounded-xl border border-border bg-background p-3" data-testid={`aff-${mode}-${a.user_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {a.name || a.email || "—"}
                    <span className="ml-1 rounded bg-secondary px-1.5 text-[10px] text-muted-foreground">{a.role || "user"}</span>
                    {mode === "active" && a.commission_percent != null && (
                      <span className="ml-1 rounded bg-emerald-100 px-1.5 text-[10px] text-emerald-700">{a.commission_percent}%</span>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {a.email || "(keine E-Mail)"}
                    {a.referral_code ? ` · Code ${a.referral_code}` : ""}
                    {a.applied_at ? ` · ${dt(a.applied_at)}` : ""}
                  </p>
                  {a.note && <p className="mt-1 text-xs italic text-foreground/80">„{a.note}“</p>}
                  {a.social_links?.length > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {a.social_links.map((s) => `${s.platform}: ${s.handle_or_url} (${s.follower_count})`).join(" · ")}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {mode !== "active" && a.source !== "application" && (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={pct[a.user_id] ?? 20}
                        onChange={(e) => setPct({ ...pct, [a.user_id]: e.target.value })}
                        className="h-8 w-16 text-sm"
                        data-testid={`aff-pct-${a.user_id}`}
                      />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                  )}
                  {(mode === "pending" || mode === "rejected") && (
                    <Button size="sm" onClick={() => decide(a, true)} disabled={!!busy} data-testid={`aff-approve-${a.user_id}`}>
                      Freischalten
                    </Button>
                  )}
                  {(mode === "pending" || mode === "active") && (
                    <Button size="sm" variant="outline" onClick={() => decide(a, false)} disabled={!!busy} data-testid={`aff-reject-${a.user_id}`}>
                      {mode === "active" ? "Deaktivieren" : "Ablehnen"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

export default function Affiliate() {
  const { data: lb, loading } = useFetch("/admin/affiliate/leaderboard?days=30&sort=total");
  const { data: payouts } = useFetch("/admin/affiliate/payout-history");
  const { data: programs } = useFetch("/admin/programs");
  if (loading) return <Loading />;
  const rows = lb?.leaderboard || []; const totals = lb?.totals || {};
  const pay = payouts?.payouts || []; const progs = programs?.programs || [];

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Admin" title="Affiliate & Programme" subtitle="Bewerbungen, Leaderboard, Auszahlungen & Programme." />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Referrer (30T)" value={totals.referrers ?? 0} />
        <Stat label="Conversions" value={totals.conversions ?? 0} />
        <Stat label="Volumen" value={money(totals.total_cents)} />
      </div>

      <AffiliateListPanel status="pending" mode="pending" title="Offene Bewerbungen" emptyText="Keine offenen Bewerbungen." />
      <AffiliateListPanel status="active" mode="active" title="Aktive Affiliates" emptyText="Noch keine aktiven Affiliates." />
      <AffiliateListPanel status="rejected" mode="rejected" title="Abgelehnte Bewerbungen" emptyText="Keine abgelehnten Bewerbungen." />

      <Section title="Leaderboard (Top 10 · 30T)" testId="admin-aff-leaderboard">
        {rows.length === 0 ? <Empty text="Noch keine Conversions." /> : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={r.referrer_id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{i + 1}. {r.name}{r.affiliate_status && r.affiliate_status !== "none" && <span className="ml-1 rounded bg-secondary px-1.5 text-[10px] text-muted-foreground">{r.affiliate_status}</span>}</p><p className="truncate text-xs text-muted-foreground">{r.email} · {r.conversions} Conv. · {r.commission_percent ?? "—"}%</p></div>
                <span className="shrink-0 text-sm font-semibold text-foreground">{money(r.total_cents)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Auszahlungs-Historie" testId="admin-aff-payouts">
        {pay.length === 0 ? <Empty text="Keine Auszahlungen." /> : (
          <div className="space-y-2">
            {pay.slice(0, 30).map((p, i) => (
              <div key={p.id || i} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-3 text-sm">
                <span className="truncate text-muted-foreground">{dt(p.created_at)} · {p.party_id || p.referrer_id || "—"}</span>
                <span className="shrink-0 font-medium text-foreground">{money(p.amount ?? p.amount_cents)}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Programme" testId="admin-aff-programs">
        {progs.length === 0 ? <Empty text="Keine Programme." /> : (
          <div className="grid gap-2 sm:grid-cols-2">
            {progs.map((p) => (
              <div key={p.id} className="rounded-xl border border-border bg-background p-3">
                <p className="text-sm font-medium text-foreground">{p.name}<span className={`ml-1 rounded px-1.5 text-[10px] ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>{p.status}</span></p>
                {p.description && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.description}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
